import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SchoolFavorite, School } from '@common-types';

import useSchoolFavoritesStore from '@store/SchoolFavoritesStore';
import useOptInStore from '@store/OptInStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { SchoolSurveyModalProps, withSchoolSurveyModal } from '@HOCs/withSchoolSurveyModal';

import { getSchoolCost, getDistanceLabel, getEnrollmentSize, getSchoolType } from '@utils/schoolInfographics';
import { getFromSchoolLocation } from '@utils/breadcrumbs';

import { SchoolCardData } from '@interfaces/schoolCard';
import { CollegeLocation } from '@interfaces/location';

import { ReducedSchoolCard } from '@components/ReducedSchoolCard';
import { LinearSchoolCard } from '@components/LinearSchoolCard';
import { useStateSpotlight } from '@hooks/useStateSpotlight';

interface SchoolCardProps extends SchoolSurveyModalProps {
  school: School;
  schoolCardType: string;
  componentId?: string;
  photoPreview?: JSX.Element;
  isFluid?: boolean;
}

const SchoolCard = (props: SchoolCardProps) => {
  const {
    school: {
      id,
      directory,
      description = '',
      enrollment,
      collegeScorecard,
      picture,
      isUshe: rawIsUshe,
      distanceMiles,
    },
    componentId,
    schoolCardType,
    onOpenSurvey,
    photoPreview,
    isFluid,
  } = props;

  const location: CollegeLocation = useLocation();
  const { spotlight } = useStateSpotlight();
  const isUshe = spotlight ? rawIsUshe : undefined;

  const [isSchoolFavorite, setIsSchoolFavorite] = useState(false);

  const { schoolFavorites } = useUserPreferencesStore((state) => state);
  const { toggleSchoolFavorite } = useSchoolFavoritesStore((state) => state);
  const { saveUserConnection } = useOptInStore((state) => state);

  useEffect(() => {
    const isSchoolFavorite = !!schoolFavorites?.find(
      (collegeFavorite: SchoolFavorite) => collegeFavorite.school.id === id,
    );

    setIsSchoolFavorite(isSchoolFavorite);
  }, [schoolFavorites, id]);

  const handleOpenSurvey = (isOpenFromNotes: boolean) => {
    onOpenSurvey?.({ id, name: directory.name, calculateLink: directory?.links.priceCalculatorUrl }, isOpenFromNotes);
  };

  const handleOpenSurveyNotes = () => {
    handleOpenSurvey(true);
  };

  const handleAddToFavorites = async () => {
    const { isLoading, isSurveyOpen } = useSchoolFavoritesStore.getState();

    if (isLoading || isSurveyOpen) return;

    await toggleSchoolFavorite(props.school);

    if (!isSchoolFavorite) {
      handleOpenSurvey(false);
    } else {
      saveUserConnection(id, false);
    }
  };

  const costPerAcademicYear = collegeScorecard?.latest?.cost?.attendance?.academic_year;
  const costPerProgramYear = collegeScorecard?.latest?.cost?.attendance?.program_year;

  const title = directory.name;
  const addToFavoritesButtonId = componentId ? `${componentId}-add-to-favorites-button` : 'add-to-favorites-button';
  const address = `${directory.address.address}. – ${directory.address.city}, ${directory.address.state}, US`;
  const schoolType = getSchoolType(directory?.demographics.carnegieSizeSetting);
  const enrollmentSize = enrollment?.totalStudentsEnrolled || 0;
  const costPerYear = getSchoolCost(costPerAcademicYear || costPerProgramYear || 0);
  const pictureUrl = picture?.pictureUrl || '';
  const distanceLabel = getDistanceLabel(distanceMiles);

  const cameFrom = getFromSchoolLocation(location.pathname);

  const schoolData: SchoolCardData = {
    directory,
    collegeScorecard,
    id,
    title,
    description,
    type: schoolType,
    enrollmentSize,
    costPerYear,
    addToFavoritesButtonId,
    url: directory.links.url,
    pictureUrl,
    address,
    cameFrom,
    isUshe,
    distanceLabel,
  };

  const schoolCardTypes = {
    reduced: (
      <ReducedSchoolCard
        onToggleAddToFavorites={handleAddToFavorites}
        isSchoolFavorite={isSchoolFavorite}
        onOpenSurveyNotes={handleOpenSurveyNotes}
        addToFavoritesButtonId={addToFavoritesButtonId}
        id={id}
        title={title}
        schoolType={schoolType}
        enrollmentSize={getEnrollmentSize(enrollmentSize)}
        costPerYear={costPerYear}
        pictureUrl={pictureUrl}
        address={address}
        isUshe={isUshe}
        distanceLabel={distanceLabel}
        cameFrom={cameFrom}
        photoPreview={photoPreview}
        isFluid={isFluid}
      />
    ),
    linear: (
      <LinearSchoolCard
        schoolCardData={schoolData}
        onToggleAddToFavorites={handleAddToFavorites}
        isSchoolFavorite={isSchoolFavorite}
        onOpenSurveyNotes={handleOpenSurveyNotes}
      />
    ),
  };

  return (schoolCardTypes as Record<string, unknown>)[schoolCardType] as JSX.Element;
};

export const SchoolCardWithModal = withSchoolSurveyModal(SchoolCard);
